/**
 * The signed-in player on the main screen and the account button of the settings screen (the account and language
 * dialog: sign out, delete the account, the language; port addition: the original showed the VK profile instead).
 *
 * The profile plate sits right of the level box of the main screen's top-left panel (the original level indicator,
 * `my_level`, which alone shows the level): only the nickname, on a plate in the panel's colours (the dark green box,
 * white bold text). The nickname is shown as the player typed it (never translated; a guest's default name is
 * translated like other game text) and shortened with an ellipsis to the width it may take; hovering the plate shows
 * the full nickname in the game's hover popup (new_interf.popup_modify 'my_nickname'). The value comes from the one
 * player state of the game (FWP.user), so the plate follows a profile reload with the next render and is hidden when
 * the game forgot the player (sign-out).
 */
import { MouseEvent, Sprite, TextField } from '@flash';
import { playerName } from '../runtime/i18n';
import { MSG_ACCOUNT_BUTTON } from './account-dialog';
import { BigButton, label, setRawTextFit } from './controls';

export const PLATE_NAME = 'my_nickname';
const PLATE_KEY = '$profile_badge';
/** Where the plate starts: right of the level box (my_level's box ends at x = 114), in the panel's row. */
export const PLATE_X = 118;
const PLATE_Y = 5;
const PLATE_H = 29;
const PAD = 8;
/** The widest nickname shown in full; a wider one gets an ellipsis (the plate ends before the sun and the clouds). */
export const NICKNAME_MAX_WIDTH = 150;
/** Colours of the level panel: the box, its darker edge and the white of the name. */
const PLATE_FILL = 0x3b4f3e;
const PLATE_EDGE = 0x2a392d;
const NAME_COLOR = 0xffffff;

export interface ProfilePlate extends Sprite {
  nicknameField: TextField;
  /** The full nickname the plate stands for (the hover popup shows it). */
  fullNickname: string;
}

/**
 * Shows `nickname` shortened with an ellipsis so that its text fits `maxWidth` pixels (measured with the field's own
 * layout). Returns whether it was shortened.
 */
export function fitNickname(tf: TextField, nickname: string, maxWidth: number): boolean {
  return setRawTextFit(tf, nickname, nickname, maxWidth);
}

/**
 * Adds the profile plate to a rendered main screen (idempotent) and fills it from the game's player state. `hover`
 * receives the plate's mouse over / out events (new_interf.mouse_event: the game's hover popup).
 */
export function profilePlate(screen: any, fwp: any, hover: (event: any) => void): ProfilePlate | null {
  if (!screen || typeof screen.addChild !== 'function' || !fwp || !fwp.user) return null;
  let plate = screen[PLATE_KEY] as ProfilePlate | undefined;
  if (!plate) {
    plate = new Sprite() as ProfilePlate;
    plate.name = PLATE_NAME;
    plate.mouseChildren = false;
    plate.nicknameField = label('', { size: 13, bold: true, color: NAME_COLOR });
    plate.addChild(plate.nicknameField);
    plate.x = PLATE_X;
    plate.y = PLATE_Y;
    plate.addEventListener(MouseEvent.MOUSE_OVER, hover);
    plate.addEventListener(MouseEvent.MOUSE_OUT, hover);
    screen.addChild(plate);
    screen[PLATE_KEY] = plate;
  }
  const name = String(fwp.user.name ?? '');
  // A guest's default name ("Игрок-7K2Q") is game text; a nickname is the player's own and stays as typed.
  plate.fullNickname = fwp.user.guest ? playerName(name) : name;
  fitNickname(plate.nicknameField, plate.fullNickname, NICKNAME_MAX_WIDTH);
  plate.visible = plate.fullNickname !== '';
  const nameW = plate.nicknameField.textWidth;
  plate.nicknameField.x = PAD - 2;
  plate.nicknameField.y = Math.round((PLATE_H - plate.nicknameField.height) / 2);
  const w = Math.round(PAD + nameW + PAD);
  const g = plate.graphics;
  g.clear();
  g.lineStyle(1, PLATE_EDGE, 1);
  g.beginFill(PLATE_FILL, 1);
  g.drawRoundRect(0, 0, w, PLATE_H, 10, 10);
  g.endFill();
  return plate;
}

const ACCOUNT_KEY = '$account_button';
/**
 * The settings screen's account button: a full-size button of the screen (its popups' beige button) at the left
 * panel's bottom, clear of the bones and in line with the right panel's two buttons, where the language list and the
 * account links were squeezed before; it opens the account and language dialog (ui/account-dialog.ts). "Save the
 * changes" of the worm names above it gets the same left and right edges (the name fields' right edge), half way
 * between the fields and it: one straight column of two buttons of one width, not two buttons 17 px apart with the
 * lower one against the bones.
 */
const ACCOUNT_X = 191;
const ACCOUNT_Y = 455;
/** Where "Save the changes" (save_names, 145 x 40) goes: its top, between the last name field and the account button. */
const SAVE_Y = 394;
/**
 * The worm names' fields (worm_name1..5: 18 px tall, 23 px apart from y 258) with a wide host (a phone, a tablet):
 * taller and further apart, from under the text above them to above "Save the changes", their text larger.
 */
const NAMES_WIDE = { y: 249, step: 27, height: 24, size: 16 };
/** The name fields' right edge (worm_name1..5: 152 + 177) where the column's buttons end. */
const COLUMN_RIGHT = 329;

/** Adds the account button to a rendered settings screen (idempotent); every signed-in player has it. */
export function accountButton(screen: any, fwp: any, onClick: () => void): BigButton | null {
  if (!screen || typeof screen.addChild !== 'function') return null;
  let button = screen[ACCOUNT_KEY] as BigButton | undefined;
  if (!button) {
    button = new BigButton(fwp, 'account_open', MSG_ACCOUNT_BUTTON, 'beige');
    narrow(button.button, COLUMN_RIGHT - ACCOUNT_X);
    button.caption.width = COLUMN_RIGHT - ACCOUNT_X;
    button.x = ACCOUNT_X;
    button.y = ACCOUNT_Y;
    button.addEventListener(MouseEvent.CLICK, () => onClick());
    screen.addChild(button);
    screen[ACCOUNT_KEY] = button;
    alignSave(screen);
    if (screen.stage && screen.stage.$visibleArea) spreadNames(screen);
  }
  button.visible = !!fwp.user && !!fwp.user.player_id;
  return button;
}

/** A button's art `width` px wide (its left edge kept). */
function narrow(b: any, width: number): void {
  const parent = b.parent;
  const r0 = b.getBounds(parent);
  if (!(r0.width > 0)) return;
  b.scaleX *= width / r0.width;
  b.x += r0.x - b.getBounds(parent).x;
}

/** The worm names' fields taller and further apart (a wide host: a finger's targets rather than 18 px lines). */
function spreadNames(screen: any): void {
  for (let i = 1; i <= 5; i++) {
    const tf = screen['worm_name' + i];
    if (!tf) continue;
    tf.y = NAMES_WIDE.y + (i - 1) * NAMES_WIDE.step;
    tf.height = NAMES_WIDE.height;
    const f = tf.defaultTextFormat;
    f.size = NAMES_WIDE.size;
    tf.defaultTextFormat = f;
    if (tf.text) tf.setTextFormat(f);
  }
}

/** Moves "Save the changes" (save_names) and its caption (the timeline's unnamed text over it) above the account button. */
function alignSave(screen: any): void {
  const save = typeof screen.getChildByName === 'function' ? screen.getChildByName('save_names') : null;
  if (!save) return;
  const b = save.getBounds(screen);
  const captions: any[] = [];
  for (let i = 0; i < screen.numChildren; i++) {
    const t = screen.getChildAt(i);
    if (!(t instanceof TextField) || !/^instance\d+$/.test(String(t.name))) continue;
    const r = t.getBounds(screen);
    const cx = r.x + r.width / 2;
    const cy = r.y + r.height / 2;
    if (cx > b.x && cx < b.x + b.width && cy > b.y && cy < b.y + b.height) captions.push(t);
  }
  narrow(save, COLUMN_RIGHT - ACCOUNT_X);
  const n = save.getBounds(screen);
  save.x += ACCOUNT_X - n.x;
  save.y += SAVE_Y - n.y;
  // (each caption keeps its place in the button: centred on it as before)
  const mid = ACCOUNT_X + (COLUMN_RIGHT - ACCOUNT_X) / 2 - (b.x + b.width / 2);
  for (const t of captions) {
    t.x += mid;
    t.y += SAVE_Y - b.y;
  }
}
